// DTO de `GET /api/contexto-lider`.
//
// El líder logueado pega a este endpoint al cargar la pantalla L1 / L2 para
// saber:
//   - Quién es (denormalización útil para no pegar a /api/me también)
//   - En qué tenant opera (mismo)
//   - Qué líneas tiene programadas HOY (en general el día actual del clock)
//   - Cuál es el turno_actual_id según la hora del clock (puede ser null si
//     está fuera de cualquier turno)
//   - Si la hora actual está fuera de turno (caso para mostrar modal "¿estás
//     capturando fuera de turno?" antes de entrar a L2)
import { z } from 'zod';
import { fuenteCapturaLineaSchema } from './ingesta_maquina.js';
import { lineaSchema } from './linea.js';
import { turnoSchema } from './turno.js';
/** Una asignación del líder hoy: línea + turno (denormalizado para FE). */
export const liderAsignacionHoySchema = z.object({
    programacion_id: z.string().uuid(),
    // Extendemos lineaSchema con es_tienda SOLO acá (no en el schema compartido) para
    // que el FE rutee: línea es_tienda → flujo de captura por corrida (planilla).
    //
    // `fuente_captura` (opcional, R12): con línea híbrida o de máquina la página
    // de captura consulta cada 10 s en lugar de cada 30 s.
    linea: lineaSchema.extend({
        es_tienda: z.boolean(),
        fuente_captura: fuenteCapturaLineaSchema.optional(),
    }),
    turno: turnoSchema,
    es_linea_principal: z.boolean(),
    /**
     * Fecha OPERATIVA de esta programación (YYYY-MM-DD, zona tenant): el día en
     * que arrancó el turno. Para turnos cruza_medianoche en su cola post-
     * medianoche es CURRENT_DATE-1. El FE DEBE usar esta fecha (no `fecha_hoy`)
     * al construir `fecha` en POST /api/registros para este bloque.
     */
    fecha_operativa: z.string(),
});
/**
 * Turno programado al líder cuya PRÓXIMA instancia operativa arranca dentro
 * de las próximas 12 h, cuando ninguna instancia de hoy está en curso ni en
 * gracia y existe programación para esa fecha operativa (design
 * horario-turno-dia §2.3, §5). `inicio_local` en fake-UTC (pared local del
 * tenant, YYYY-MM-DDTHH:MM).
 */
export const proximoInicioSchema = z
    .object({
    turno: z.object({
        id: z.string().uuid(),
        numero: z.number().int(),
        nombre: z.string(),
    }),
    fecha_operativa: z.string(),
    inicio_local: z.string(),
    minutos_para_inicio: z.number().int(),
})
    .nullable();
/**
 * Se llena SOLO si `proximo_inicio` es nulo y hay programaciones de hoy cuyo
 * horario resuelto es `no_opera` (design horario-turno-dia §5).
 */
export const diaSinTurnoSchema = z
    .object({
    fecha_operativa: z.string(),
    turnos: z.array(z.object({
        id: z.string().uuid(),
        numero: z.number().int(),
        nombre: z.string(),
    })),
})
    .nullable();
export const contextoLiderSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({
        id: z.string().uuid(),
        nombre: z.string(),
    }),
    programaciones_hoy: z.array(liderAsignacionHoySchema),
    /** Turno cuyo rango horario contiene el clock actual del servidor. null si fuera de todos. */
    turno_actual_id: z.string().uuid().nullable(),
    /** Conveniencia: true si turno_actual_id es null o no matchea ninguna programación del día. */
    es_fuera_turno: z.boolean(),
    /** Hora "ahora" del servidor en HH:MM (zona del tenant). FE no necesita Date.now propio. */
    hora_servidor: z.string(),
    /**
     * Fecha de HOY del servidor en YYYY-MM-DD (zona del tenant via `SET LOCAL
     * TimeZone`). El FE debe usar este valor para construir `fecha` en POST
     * /api/registros — no `new Date().toISOString().slice(0,10)` (que usa UTC).
     */
    fecha_hoy: z.string(),
    /** Excluyente con `dia_sin_turno` — evaluado primero (design §5). */
    proximo_inicio: proximoInicioSchema,
    /** Excluyente con `proximo_inicio` — evaluado solo si ese es nulo (design §5). */
    dia_sin_turno: diaSinTurnoSchema,
});
//# sourceMappingURL=contexto_lider.js.map